import { marked } from 'marked'
import { useEffect, useMemo, useRef, useState } from 'react'
import { PARTICIPANT_COLORS, hashString } from './participantColors.js'
import './Message.css'

// Configure marked for code highlighting + XSS prevention.
// highlight.js is lazy-loaded (~300KB gzipped) — eager markdown render escapes
// code blocks via escapeHtml; when hljs resolves we asynchronously re-render
// the highlighted HTML via a memoized cache keyed on content.
const escapeHtml = (s: string) =>
  (s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

let hljsPromise: Promise<any> | null = null
const loadHljs = () => {
  if (!hljsPromise) {
    hljsPromise = import(/* webpackChunkName: "hljs" */ 'highlight.js').then(m => m.default || m)
  }
  return hljsPromise
}

marked.setOptions({ breaks: true, gfm: true })

// XSS prevention: escape raw HTML blocks + block javascript: links
const escapeAttr = (s: string) => escapeHtml(s).replace(/"/g, '&quot;')
marked.use({
  renderer: {
    html(token: any) {
      return escapeHtml(token.text)
    },
    link(token: any) {
      const href = (token.href || '').trim()
      if (/^\s*javascript\s*:/i.test(href)) return escapeHtml(token.text)
      const title = token.title ? ` title="${escapeAttr(token.title)}"` : ''
      return `<a href="${escapeAttr(href)}"${title} target="_blank" rel="noopener noreferrer">${token.tokens ? this.parser.parseInline(token.tokens) : escapeHtml(token.text)}</a>`
    },
  },
})

export const Message = ({
  role,
  content,
  timestamp,
  isError = false,
  metadata,
  participantId,
  participantName,
  participantTypeName,
  targetName,
  hostName = 'MyAgent',
}: any) => {
  const isUser = role === 'user'
  const isParticipant = role === 'participant'
  const participantColor = useMemo(() => {
    if (!isParticipant || !participantId) return null
    return PARTICIPANT_COLORS[hashString(participantId) % PARTICIPANT_COLORS.length]
  }, [isParticipant, participantId])

  // Lazy-load highlight.js only when this message contains a <pre><code> block.
  // Re-runs when `content` changes so newly streamed code blocks get highlighted.
  const contentRef = useRef<HTMLDivElement | null>(null)
  // biome-ignore lint/correctness/useExhaustiveDependencies: content is used via DOM ref, not direct closure
  useEffect(() => {
    const root = contentRef.current
    if (!root) return
    const blocks = root.querySelectorAll('pre code')
    if (blocks.length === 0) return
    let cancelled = false
    loadHljs().then(hljs => {
      if (cancelled) return
      blocks.forEach(block => {
        try {
          ;(hljs as any).highlightElement(block as HTMLElement)
        } catch (e) {
          console.error('Highlight error:', e)
        }
      })
    })
    return () => {
      cancelled = true
    }
  }, [content])

  const renderContent = () => {
    if (isUser) {
      return <p className="message-text">{content}</p>
    }

    if (isParticipant) {
      // Tool calls and role requests: monospace with collapsible result
      if (metadata?.type === 'tool_call' || metadata?.type === 'role_request') {
        return <ParticipantContent content={content} metadata={metadata} targetName={targetName} />
      }
      // All other participant messages: markdown rendering
      if (!content) return <p className="message-text message-empty">(No content)</p>
      const html = marked(content)
      return (
        <div
          ref={contentRef}
          className="message-text message-markdown"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      )
    }

    // Legacy assistant fallback
    if (!content) return <p className="message-text message-empty">(No content)</p>
    const html = marked(content)
    return (
      <div className="message-text message-markdown" dangerouslySetInnerHTML={{ __html: html }} />
    )
  }

  const renderMetadata = () => {
    if (isUser || !metadata) return null
    if (isParticipant && metadata?.type !== 'assistant_response') return null

    const { usage, modelInfo } = metadata
    const hasData = usage || modelInfo

    if (!hasData) return null

    const parts: string[] = []

    if (modelInfo?.selectedModel) {
      parts.push(modelInfo.selectedModel)
    }

    if (usage?.totalTokens) {
      parts.push(`${usage.totalTokens.toLocaleString()} tokens`)
    }

    if (metadata.durationMs) {
      const sec = (metadata.durationMs / 1000).toFixed(1)
      parts.push(`${sec}s`)
    }

    if (parts.length === 0) return null

    const tooltipParts: string[] = []
    if (modelInfo?.selectedModel) {
      tooltipParts.push(`Model: ${modelInfo.selectedModel}`)
      if (modelInfo.reason) tooltipParts.push(`Reason: ${modelInfo.reason}`)
    }
    if (usage) {
      if (usage.promptTokens) tooltipParts.push(`Input: ${usage.promptTokens.toLocaleString()}`)
      if (usage.completionTokens)
        tooltipParts.push(`Output: ${usage.completionTokens.toLocaleString()}`)
      if (usage.totalTokens) tooltipParts.push(`Total: ${usage.totalTokens.toLocaleString()}`)
    }

    return (
      <div className="message-metadata" title={tooltipParts.join('\n')}>
        {parts.join(' \u00b7 ')}
      </div>
    )
  }

  const roleLabel = isUser ? 'You' : participantName || hostName
  const showTypeName =
    isParticipant && participantTypeName && participantName !== participantTypeName
  const isGenerating = isParticipant && metadata?.type === 'progress'

  return (
    <div
      className={`message message-${role} ${isError ? 'message-error' : ''}`}
      style={participantColor ? ({ '--participant-color': participantColor } as any) : undefined}
    >
      <div className="message-header">
        <span className="message-role">
          {roleLabel}
          {showTypeName && <span className="message-role-type"> ({participantTypeName})</span>}
        </span>
        {timestamp && (
          <span className="message-time">
            {new Date(timestamp).toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        )}
      </div>
      <div className="message-content">
        {renderContent()}
        {isGenerating && (
          <div className="message-generating">
            Generating
            <span className="generating-dots" />
          </div>
        )}
        {renderMetadata()}
      </div>
    </div>
  )
}

/**
 * Participant message content — shows action and collapsible result
 */
const ParticipantContent = ({ content, metadata, targetName }: any) => {
  const [expanded, setExpanded] = useState(false)
  const hasResult = metadata?.result !== undefined
  const isRequest = metadata?.type === 'role_request'

  return (
    <div className="participant-content">
      <div className="participant-action">
        <span className="participant-action-text">
          {isRequest && targetName ? `@${targetName} ` : ''}
          {content}
        </span>
        {hasResult && (
          <button
            className="participant-detail-toggle"
            onClick={() => setExpanded(!expanded)}
            aria-expanded={expanded}
          >
            {expanded ? '\u25BE' : '\u25B8'}
          </button>
        )}
      </div>
      {expanded && hasResult && (
        <pre className="participant-result">
          {typeof metadata.result === 'string'
            ? metadata.result
            : JSON.stringify(metadata.result, null, 2)}
        </pre>
      )}
    </div>
  )
}
